import React, { useState, useEffect } from "react";
import { fetchWrapper } from "utils/apiFetch";

function DTHRecharge() {
  const [mobile, setMobile] = useState("");
  const [amount, setAmount] = useState("");
  const [pin, setMPIN] = useState("");
  const [operator, setOperator] = useState("");
  const [operators, setOperators] = useState([]);
  const [circles, setCircles] = useState([]);
  const [error, setError] = useState("");
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [dthDetails, setDthDetails] = useState(null);

  // Plan-related states
  const [planData, setPlanData] = useState(null);
  const [activeTab, setActiveTab] = useState("TOPUP");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // DTH Channel Plan states
  const [dthChannelData, setDthChannelData] = useState(null);
  const [dthChannelLoading, setDthChannelLoading] = useState(false);
  const [dthChannelSearchTerm, setDthChannelSearchTerm] = useState("");
  const [activeDthTab, setActiveDthTab] = useState("Plan");

  // DTH R Offer states
  const [dthROfferData, setDthROfferData] = useState(null);
  const [dthROfferLoading, setDthROfferLoading] = useState(false);
  const [dthROfferSearchTerm, setDthROfferSearchTerm] = useState("");

  // Helper function to create valid CSS selector IDs
  const createValidId = (text) => {
    return text.replace(/[^a-zA-Z0-9]/g, '_').replace(/^[0-9]/, 'tab_$&');
  };

  // Fetch Operators & Circles on page load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const opRes = await fetchWrapper("https://pro.mokshiq.in/fetch_dth_operator?type=dth");
        const opJson = opRes;
        setOperators(opJson.data || []);
      } catch (err) {
        console.error("Error loading operators/circles", err);
      }
    };

    fetchData();
  }, []);

  // Auto-detect Operator & Circle when mobile reaches required digits
  useEffect(() => {
    if (!operator || !mobile) return;
  
    let requiredLength = 10; // default
    if (operator.toLowerCase() === "dish tv" || operator.toLowerCase() === "sun direct") {
      requiredLength = 11;
    } else if (operator.toLowerCase() === "videocon") {
      requiredLength = 9;
    }
  
    if (mobile.length === requiredLength) {
      const fetchDthInfo = async () => {
        try {
          setRechargeLoading(true);
          const res = await fetchWrapper(
            `https://pro.mokshiq.in/fetch_dth_info?phone=${mobile}&operator=${operator}`
          );
          
          const json = res;
  
          if (json?.status && json?.data?.records?.length > 0) {
            setDthDetails(json.data.records[0]);
          } else {
            setDthDetails(null);
            showToast("No details found for this DTH number", "warning");
          }
        } catch (err) {
          console.error("DTH Info Lookup failed:", err);
          showToast("Failed to fetch DTH details", "danger");
        } finally {
          setRechargeLoading(false);
        }
      };
  
      fetchDthInfo();
    }
  }, [mobile, operator]);
  
  // Fetch plan data
  const fetchPlanData = async () => {
    if (!operator) {
      showToast("Please select operator first", "warning");
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetchWrapper(
        `https://pro.mokshiq.in/fetch_dth_plan?operator=${encodeURIComponent(operator)}`
      );
      const data = response;
      
      if (data.status && data.data && data.data.records) {
        setPlanData(data.data.records);
        // Set first category as active tab
        const firstCategory = Object.keys(data.data.records)[0];
        if (firstCategory) {
          setActiveTab(firstCategory);
        }
      } else {
        setPlanData(null);
        showToast("No plans found for this operator", "warning");
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
      setPlanData(null);
      showToast("Failed to fetch plans", "danger");
    } finally {
      setLoading(false);
    }
  };

  // Fetch DTH Channel Plans
  const fetchDthChannelPlans = async () => {
    if (!operator) {
      showToast("Please select operator first", "warning");
      return;
    }
  
    setDthChannelLoading(true);
    try {
      const response = await fetchWrapper(`https://pro.mokshiq.in/fetch_dth_plan_channel?operator=${encodeURIComponent(operator)}`);
      const data = response;
      
      if (data.status && data.data && data.data.records) {
        setDthChannelData(data.data.records);
        // Set first category as active tab
        const firstCategory = Object.keys(data.data.records)[0];
        if (firstCategory) {
          setActiveDthTab(firstCategory);
        }
      } else {
        setDthChannelData(null);
        showToast("No channel plans found for this operator", "warning");
      }
    } catch (err) {
      console.error("Error fetching DTH channel plans:", err);
      setDthChannelData(null);
      showToast("Failed to fetch DTH channel plans", "danger");
    } finally {
      setDthChannelLoading(false);
    }
  };

  // Fetch DTH R Offer data
  const fetchDthROfferData = async () => {
    if (!operator) {
      showToast("Please select operator first", "warning");
      return;
    }
  
    setDthROfferLoading(true);
    try {
      const response = await fetchWrapper(`https://pro.mokshiq.in/fetch_dth_roffer?operator=${encodeURIComponent(operator)}`);
      const data = response;
      
      if (data.status && data.data && data.data.records) {
        if (data.data.records.status === 0) {
          // Plan not available
          setDthROfferData(null);
          showToast(data.data.records.desc || "DTH R Offer not available for this operator", "warning");
        } else {
          setDthROfferData(data.data.records);
        }
      } else {
        setDthROfferData(null);
        showToast("No DTH R Offer found for this operator", "warning");
      }
    } catch (err) {
      console.error("Error fetching DTH R Offer:", err);
      setDthROfferData(null);
      showToast("Failed to fetch DTH R Offer", "danger");
    } finally {
      setDthROfferLoading(false);
    }
  };

  // DTH Heavy Refresh function
  const performDthHeavyRefresh = async () => {
    if (!operator || !mobile) {
      showToast("Please select operator and enter DTH number first", "warning");
      return;
    }
  
    setRechargeLoading(true);
    try {
      const response = await fetchWrapper(`https://pro.mokshiq.in/dth_heavy_refresh?operator=${encodeURIComponent(operator)}&phone=${mobile}`);
      const data = response;
      
      if (data.status) {
        showToast("DTH Heavy refresh completed successfully", "success");
        // Optionally refresh DTH details after heavy refresh
        if (mobile.length >= 9) {
          const fetchDthInfo = async () => {
            try {
              const res = await fetchWrapper(
                `https://pro.mokshiq.in/fetch_dth_info?phone=${mobile}&operator=${operator}`
              );
              const json = res;;
              if (json?.status && json?.data?.records?.length > 0) {
                setDthDetails(json.data.records[0]);
              }
            } catch (err) {
              console.error("DTH Info Lookup failed:", err);
            }
          };
          fetchDthInfo();
        }
      } else {
        showToast(data.message || "DTH Heavy refresh failed", "danger");
      }
    } catch (err) {
      console.error("Error in DTH Heavy refresh:", err);
      showToast("Failed to perform DTH Heavy refresh", "danger");
    } finally {
      setRechargeLoading(false);
    }
  };

  // Handle mobile number input (only digits)
  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setMobile(value);
  };

  // Handle plan selection
  const handlePlanSelect = (planAmount) => {
    setAmount(planAmount);
    const offcanvasElement = document.getElementById('offcanvasBottom1');
    if (offcanvasElement && window.bootstrap) {
      const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      }
    }
  };

  // Handle DTH channel plan selection
  const handleDthChannelSelect = (planAmount) => {
    setAmount(planAmount);
    const offcanvasElement = document.getElementById('offcanvasBottom2');
    if (offcanvasElement && window.bootstrap) {
      const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      }
    }
  };

  // Handle DTH R Offer selection
  const handleDthROfferSelect = (planAmount) => {
    setAmount(planAmount);
    const offcanvasElement = document.getElementById('offcanvasBottom3');
    if (offcanvasElement && window.bootstrap) {
      const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvasElement);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      }
    }
  };

  // Filter plans
  const filterPlans = (plans) => {
    if (!searchTerm) return plans;
    return plans.filter(plan => 
      (plan.rs && Object.values(plan.rs).some(price => price.toString().includes(searchTerm))) ||
      (plan.desc && plan.desc.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (plan.plan_name && plan.plan_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  // Filter DTH channel plans
  const filterDthChannelPlans = (plans) => {
    if (!dthChannelSearchTerm) return plans;
    return plans.filter(plan => 
      (plan.rs && Object.values(plan.rs).some(price => price.toString().includes(dthChannelSearchTerm))) ||
      (plan.desc && plan.desc.toLowerCase().includes(dthChannelSearchTerm.toLowerCase())) ||
      (plan.plan_name && plan.plan_name.toLowerCase().includes(dthChannelSearchTerm.toLowerCase()))
    );
  };

  // Filter DTH R Offer data
  const filterDthROfferData = (data) => {
    if (!dthROfferSearchTerm || !Array.isArray(data)) return data;
    return data.filter(item => 
      (item.rs && item.rs.toString().includes(dthROfferSearchTerm)) ||
      (item.desc && item.desc.toLowerCase().includes(dthROfferSearchTerm.toLowerCase())) ||
      (item.plan_name && item.plan_name.toLowerCase().includes(dthROfferSearchTerm.toLowerCase()))
    );
  };

  const showToast = (message, type = "primary") => {
    const alertDiv = document.createElement("div");
    alertDiv.className = `alert alert-${type} alert-dismissible cookies-alert position-fixed top-0 end-0 m-3 shadow`;
    alertDiv.setAttribute("role", "alert");
    alertDiv.style.zIndex = 2000;

    alertDiv.innerHTML = `
      <div class="d-flex justify-content-between align-items-center">
        <p class="mb-0 flex-grow-1">${message}</p>
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>
    `;
    document.body.appendChild(alertDiv);
    setTimeout(() => alertDiv?.remove(), 3000);
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (mobile.length < 9) {
      setError("DTH number must be at least 9 digits.");
      return;
    }
    if (!amount) {
      setError("Please enter recharge amount.");
      return;
    }
    if (!operator) {
      setError("Please select operator.");
      return;
    }
    
    setRechargeLoading(true);
    try {
        const formData = new FormData();
        formData.append("number", mobile);
        formData.append("operator", operator);
        formData.append("amount", amount);
        formData.append("user_id", 3);
        formData.append("pin", pin);
    
        const response = await fetchWrapper("https://pro.mokshiq.in/create_dth_recharge", {
          method: "POST",
           headers: {
          'Authorization': `Bearer ${localStorage.getItem("authToken")}`,
        },
          body: formData,
        });
    
        const data = await response.json();
    
        if (data.status) {
            showToast(data.message || "Success!", "success");
            setMobile("");
            setAmount("");
            setOperator("");
            setError("");
        } else {
            showToast(data.message || "Something went wrong!", "danger");
        }
      } catch (err) {
        console.error("Recharge API Error:", err);
        showToast("Something went wrong!", "danger");
      } finally {
        setRechargeLoading(false);
      }
  };

  const [showPin, setShowPin] = useState(false);

  return (
    <main>
      <div className="container-fluid p-3 p-md-4">
        {/* Modern Branded Hero Banner */}
        <div
          className="card border-0 mb-4 overflow-hidden position-relative shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f5efff 0%, #e0e7ff 100%)",
            borderRadius: "16px"
          }}
        >
          <div className="card-body p-3.5 px-4 position-relative z-1">
            <div className="row align-items-center">
              <div className="col-12 col-md-7 col-lg-8">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "14px",
                      backgroundColor: "#e0e7ff",
                      color: "#7e57c2"
                    }}
                  >
                    <i className="ph-duotone ph-television-simple" style={{ fontSize: "24px" }}></i>
                  </div>
                  <div>
                    <h4 className="fw-extrabold mb-1" style={{ color: "#0f172a", fontSize: "20px" }}>
                      DTH Recharge
                    </h4>
                    <ul className="app-line-breadcrumbs mb-0">
                      <li>
                        <a className="f-s-13 f-w-500" href="#">
                          <span>
                            <i className="ph-duotone ph-briefcase-metal f-s-14"></i> Services
                          </span>
                        </a>
                      </li>
                      <li className="active">
                        <a className="f-s-13 f-w-600" href="#" style={{ color: "#7e57c2" }}>
                          DTH Recharge
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-5 col-lg-4 text-end d-none d-md-block">
                <img
                  src="/assets/images/dth_3d_banner.png"
                  alt="DTH 3D Illustration"
                  style={{
                    maxHeight: "105px",
                    objectFit: "contain",
                    filter: "drop-shadow(0px 8px 16px rgba(126, 87, 194, 0.2))"
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="row">
          <div className="col-12">
            <div
              className="card border-0 shadow-sm overflow-hidden"
              style={{ borderRadius: "16px", backgroundColor: "#ffffff" }}
            >
              {rechargeLoading && (
                <div 
                  className="page-loader-overlay d-flex justify-content-center align-items-center"
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255,255,255,0.75)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 3000,
                  }}
                >
                  <div className="spinner-border text-primary" role="status" style={{ color: '#7e57c2' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
              
              <div className="card-body p-4">
                {/* Form Header */}
                <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "12px",
                      backgroundColor: "#f3e8ff",
                      color: "#7e57c2"
                    }}
                  >
                    <i className="ph-duotone ph-broadcast" style={{ fontSize: "22px" }}></i>
                  </div>
                  <div>
                    <h5 className="fw-bold mb-0" style={{ color: "#0f172a", fontSize: "16px" }}>
                      Recharge Details
                    </h5>
                    <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                      Enter operator, DTH number and amount to proceed.
                    </p>
                  </div>
                </div>

                <div className="app-form">
                  <div className="row g-3">
                    {/* Operator Dropdown */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold mb-1.5" style={{ fontSize: "13px", color: "#334155" }}>
                        Select Operator
                      </label>
                      <div className="input-group">
                        <span className="input-group-text border-end-0" style={{ backgroundColor: "#f3e8ff", borderColor: "#cbd5e1" }}>
                          <i className="ph-bold ph-broadcast" style={{ color: "#7e57c2", fontSize: "16px" }}></i>
                        </span>
                        <select
                          className="form-select border-start-0 py-2"
                          value={operator}
                          onChange={(e) => setOperator(e.target.value)}
                          required
                          style={{ borderRadius: "0 12px 12px 0", fontSize: "13.5px" }}
                        >
                          <option value="">-- Select Operator --</option>
                          {operators.map((op, i) => (
                            <option key={i} value={op.name}>
                              {op.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* DTH Number */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold mb-1.5" style={{ fontSize: "13px", color: "#334155" }}>
                        DTH Number
                      </label>
                      <div className="input-group">
                        <span className="input-group-text border-end-0" style={{ backgroundColor: "#f3e8ff", borderColor: "#cbd5e1" }}>
                          <i className="ph-bold ph-credit-card" style={{ color: "#7e57c2", fontSize: "16px" }}></i>
                        </span>
                        <input
                          className="form-control border-start-0 border-end-0 py-2"
                          placeholder="Enter Your DTH Number"
                          value={mobile}
                          onChange={handleMobileChange}
                          required
                          type="text"
                          maxLength="11"
                          style={{ fontSize: "13.5px" }}
                        />
                        <span className="input-group-text border-start-0 bg-white" style={{ borderColor: "#cbd5e1" }}>
                          <i className="ph-bold ph-user-focus text-muted" style={{ fontSize: "16px" }}></i>
                        </span>
                      </div>
                    </div>

                    {/* Recharge Amount */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold mb-1.5" style={{ fontSize: "13px", color: "#334155" }}>
                        Recharge Amount (₹)
                      </label>
                      <div className="input-group">
                        <span className="input-group-text border-end-0" style={{ backgroundColor: "#f3e8ff", borderColor: "#cbd5e1" }}>
                          <i className="ph-bold ph-currency-inr" style={{ color: "#7e57c2", fontSize: "16px" }}></i>
                        </span>
                        <input
                          className="form-control border-start-0 py-2"
                          placeholder="Enter amount (e.g. 300)"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          required
                          type="text"
                          style={{ borderRadius: "0 12px 12px 0", fontSize: "13.5px" }}
                        />
                      </div>
                    </div>

                    {/* Transaction Pin */}
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold mb-1.5" style={{ fontSize: "13px", color: "#334155" }}>
                        Transaction Pin
                      </label>
                      <div className="input-group">
                        <span className="input-group-text border-end-0" style={{ backgroundColor: "#f3e8ff", borderColor: "#cbd5e1" }}>
                          <i className="ph-bold ph-lock-key" style={{ color: "#7e57c2", fontSize: "16px" }}></i>
                        </span>
                        <input
                          className="form-control border-start-0 border-end-0 py-2"
                          placeholder="Enter transaction pin"
                          value={pin}
                          onChange={(e) => setMPIN(e.target.value)}
                          required
                          type={showPin ? "text" : "password"}
                          style={{ fontSize: "13.5px" }}
                        />
                        <button
                          type="button"
                          className="input-group-text border-start-0 bg-white"
                          style={{ borderColor: "#cbd5e1", cursor: "pointer" }}
                          onClick={() => setShowPin(!showPin)}
                        >
                          <i className={`ph-bold ${showPin ? "ph-eye" : "ph-eye-slash"} text-muted`} style={{ fontSize: "16px" }}></i>
                        </button>
                      </div>
                    </div>

                    {/* Action Buttons Row */}
                    <div className="col-12 mt-3">
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        {/* DTH R Offer */}
                        <button
                          type="button"
                          className="btn dth-action-btn-roffer d-inline-flex align-items-center gap-1.5 px-3 py-1.5"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvasBottom3"
                          onClick={fetchDthROfferData}
                        >
                          <i className="ph-bold ph-lightning" style={{ fontSize: "14px" }}></i>
                          <span>DTH R Offer</span>
                        </button>

                        {/* Channel Plans */}
                        <button
                          type="button"
                          className="btn dth-action-btn-channel d-inline-flex align-items-center gap-1.5 px-3 py-1.5"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvasBottom2"
                          onClick={fetchDthChannelPlans}
                        >
                          <i className="ph-bold ph-television" style={{ fontSize: "14px" }}></i>
                          <span>Channel Plans</span>
                        </button>

                        {/* Fetch Plan */}
                        <button
                          type="button"
                          className="btn dth-action-btn-plan d-inline-flex align-items-center gap-1.5 px-3 py-1.5"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvasBottom1"
                          onClick={fetchPlanData}
                        >
                          <i className="ph-bold ph-clipboard-text" style={{ fontSize: "14px" }}></i>
                          <span>Fetch Plan</span>
                        </button>

                        {/* Heavy Refresh */}
                        <button
                          type="button"
                          className="btn dth-action-btn-refresh d-inline-flex align-items-center gap-1.5 px-3 py-1.5"
                          onClick={performDthHeavyRefresh}
                        >
                          <i className="ph-bold ph-arrows-clockwise" style={{ fontSize: "14px" }}></i>
                          <span>Heavy Refresh</span>
                        </button>
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="col-12 mt-2">
                        <div className="alert alert-danger py-2 px-3 small border-0 rounded-3 mb-0">
                          <i className="ph-bold ph-warning-circle me-1.5"></i>
                          {error}
                        </div>
                      </div>
                    )}

                    {/* Submit & Reset Buttons */}
                    <div className="col-12 mt-4 pt-3 border-top">
                      <div className="d-flex align-items-center gap-3">
                        <button
                          className="btn btn-dth-submit py-2 px-4 fw-bold d-inline-flex align-items-center justify-content-center gap-2"
                          type="button"
                          onClick={handleSubmit}
                          style={{ minWidth: "140px", height: "38px" }}
                        >
                          <i className="ph-bold ph-paper-plane-tilt" style={{ fontSize: "16px" }}></i>
                          <span>Submit</span>
                        </button>
                        <button
                          className="btn btn-dth-reset py-2 px-4 fw-bold d-inline-flex align-items-center justify-content-center gap-2"
                          type="button"
                          onClick={() => {
                            setMobile("");
                            setAmount("");
                            setOperator("");
                            setError("");
                          }}
                          style={{ minWidth: "140px", height: "38px" }}
                        >
                          <i className="ph-bold ph-arrows-counter-clockwise" style={{ fontSize: "16px" }}></i>
                          <span>Reset</span>
                        </button>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
              
              {dthDetails && (
                <div className="card-footer bg-light p-4 border-top">
                  <div className="d-flex align-items-center gap-2.5 mb-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{ width: "32px", height: "32px", backgroundColor: "#f3e8ff", color: "#7e57c2" }}
                    >
                      <i className="ph-bold ph-user" style={{ fontSize: "16px" }}></i>
                    </div>
                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "15px" }}>Customer Details</h6>
                  </div>
                  <div className="row g-3">
                    <div className="col-12 col-sm-6 col-md-4">
                      <div className="p-3 bg-white border rounded-3 shadow-xs">
                        <small className="text-muted d-block mb-1">Customer Name</small>
                        <span className="fw-bold text-dark">{dthDetails.customerName || "N/A"}</span>
                      </div>
                    </div>
                    <div className="col-12 col-sm-6 col-md-4">
                      <div className="p-3 bg-white border rounded-3 shadow-xs">
                        <small className="text-muted d-block mb-1">Balance</small>
                        <span className="fw-bold text-success">₹{dthDetails.Balance || "0"}</span>
                      </div>
                    </div>
                    <div className="col-12 col-sm-6 col-md-4">
                      <div className="p-3 bg-white border rounded-3 shadow-xs">
                        <small className="text-muted d-block mb-1">Status</small>
                        <span className="badge bg-success-subtle text-success px-2.5 py-1 rounded-pill fw-semibold">
                          {dthDetails.status || "Active"}
                        </span>
                      </div>
                    </div>
                    <div className="col-12 col-sm-6 col-md-4">
                      <div className="p-3 bg-white border rounded-3 shadow-xs">
                        <small className="text-muted d-block mb-1">Plan Name</small>
                        <span className="fw-bold text-dark">{dthDetails.planname || "N/A"}</span>
                      </div>
                    </div>
                    <div className="col-12 col-sm-6 col-md-4">
                      <div className="p-3 bg-white border rounded-3 shadow-xs">
                        <small className="text-muted d-block mb-1">Monthly Recharge</small>
                        <span className="fw-bold" style={{ color: "#7e57c2" }}>
                          ₹{dthDetails.MonthlyRecharge || "0"}
                        </span>
                      </div>
                    </div>
                    <div className="col-12 col-sm-6 col-md-4">
                      <div className="p-3 bg-white border rounded-3 shadow-xs">
                        <small className="text-muted d-block mb-1">Next Recharge Date</small>
                        <span className="fw-bold text-dark">{dthDetails.NextRechargeDate || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Offcanvas for Plan Selection */}
        <div 
          className="offcanvas offcanvas-bottom rounded-top-4 border-0 shadow-lg"
          id="offcanvasBottom1"
          tabIndex="-1"
          style={{ height: '75vh', maxWidth: '800px', margin: '0 auto', backgroundColor: '#ffffff' }}
        >
          <div className="offcanvas-header border-bottom py-3 px-4 bg-white sticky-top">
            <h5 className="offcanvas-title fw-bold text-dark mb-0">Select DTH Plan</h5>
            <button 
              className="btn-close text-secondary"
              data-bs-dismiss="offcanvas"
              type="button"
              aria-label="Close"
            ></button>
          </div>
          <div className="offcanvas-body p-4" style={{ backgroundColor: '#f8f9fa' }}>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted small">Fetching DTH plans...</p>
              </div>
            ) : planData ? (
              <>
                {/* Search Box */}
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control form-control-lg bg-white border shadow-sm fs-6"
                    placeholder="🔍 Search DTH plans..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Tabs */}
                <div className="overflow-auto mb-3">
                  <ul className="nav nav-pills flex-nowrap gap-2 pb-2">
                    {Object.keys(planData).map((category) => {
                      const validId = createValidId(category);
                      const isAct = activeTab === category;
                      return (
                        <li className="nav-item" key={category}>
                          <button
                            className={`nav-link text-nowrap py-2 px-3 rounded-pill fw-semibold border ${
                              isAct ? 'btn-primary text-white border-primary shadow-sm' : 'bg-white text-dark border-light-subtle'
                            }`}
                            data-bs-toggle="tab"
                            data-bs-target={`#${validId}`}
                            type="button"
                            onClick={() => setActiveTab(category)}
                            style={{ fontSize: '13px' }}
                          >
                            {category.replace(/[^a-zA-Z]/g, ' ')}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Tab Content */}
                <div className="tab-content">
                  {Object.entries(planData).map(([category, plans]) => {
                    const validId = createValidId(category);
                    return (
                      <div
                        key={category}
                        className={`tab-pane fade ${activeTab === category ? 'show active' : ''}`}
                        id={validId}
                      >
                        <div className="d-flex flex-column gap-3">
                          {filterPlans(plans).map((plan, index) => (
                            <div key={index} className="card plan-card border shadow-sm rounded-3 overflow-hidden bg-white">
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-start gap-3">
                                  <div className="flex-grow-1">
                                    <h6 className="fw-bold text-primary mb-2">
                                      {plan.plan_name}
                                    </h6>
                                    <p className="card-text text-secondary small mb-2 lh-sm">
                                      {plan.desc}
                                    </p>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                      {plan.rs && Object.entries(plan.rs).map(([duration, price]) => (
                                        <div key={duration} className="d-flex align-items-center bg-light border rounded px-2 py-1">
                                          <span className="fw-bold text-dark me-2 small">
                                            {duration}: ₹{price}
                                          </span>
                                          <button
                                            className="btn btn-sm btn-primary rounded-pill px-2.5 py-0.5"
                                            onClick={() => handlePlanSelect(price)}
                                            style={{ fontSize: '11px' }}
                                          >
                                            Select
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                    {plan.last_update && (
                                      <small className="text-muted extra-small">
                                        Updated: {plan.last_update}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {filterPlans(plans).length === 0 && (
                          <div className="text-center py-5 text-muted">
                            No plans found matching your search.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-5 text-muted">
                Click "Fetch Plan" to load available plans.
              </div>
            )}
          </div>
        </div>

        {/* Offcanvas for DTH Channel Plans */}
        <div 
          className="offcanvas offcanvas-bottom rounded-top-4 border-0 shadow-lg"
          id="offcanvasBottom2"
          tabIndex="-1"
          style={{ height: '75vh', maxWidth: '800px', margin: '0 auto', backgroundColor: '#ffffff' }}
        >
          <div className="offcanvas-header border-bottom py-3 px-4 bg-white sticky-top">
            <h5 className="offcanvas-title fw-bold text-dark mb-0">DTH Channel Plans</h5>
            <button 
              className="btn-close text-secondary"
              data-bs-dismiss="offcanvas"
              type="button"
              aria-label="Close"
            ></button>
          </div>
          <div className="offcanvas-body p-4" style={{ backgroundColor: '#f8f9fa' }}>
            {dthChannelLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted small">Fetching DTH channel plans...</p>
              </div>
            ) : dthChannelData ? (
              <>
                {/* Search Box */}
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control form-control-lg bg-white border shadow-sm fs-6"
                    placeholder="🔍 Search channel plans..."
                    value={dthChannelSearchTerm}
                    onChange={(e) => setDthChannelSearchTerm(e.target.value)}
                  />
                </div>

                {/* Tabs */}
                <div className="overflow-auto mb-3">
                  <ul className="nav nav-pills flex-nowrap gap-2 pb-2">
                    {Object.keys(dthChannelData).map((category) => {
                      const validId = createValidId(category);
                      const isAct = activeDthTab === category;
                      return (
                        <li className="nav-item" key={category}>
                          <button
                            className={`nav-link text-nowrap py-2 px-3 rounded-pill fw-semibold border ${
                              isAct ? 'btn-primary text-white border-primary shadow-sm' : 'bg-white text-dark border-light-subtle'
                            }`}
                            data-bs-toggle="tab"
                            data-bs-target={`#dth_${validId}`}
                            type="button"
                            onClick={() => setActiveDthTab(category)}
                            style={{ fontSize: '13px' }}
                          >
                            {category.replace(/[^a-zA-Z]/g, ' ')}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Tab Content */}
                <div className="tab-content">
                  {Object.entries(dthChannelData).map(([category, plans]) => {
                    const validId = createValidId(category);
                    return (
                      <div
                        key={category}
                        className={`tab-pane fade ${activeDthTab === category ? 'show active' : ''}`}
                        id={`dth_${validId}`}
                      >
                        <div className="d-flex flex-column gap-3">
                          {filterDthChannelPlans(plans).map((plan, index) => (
                            <div key={index} className="card plan-card border shadow-sm rounded-3 overflow-hidden bg-white">
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-start gap-3">
                                  <div className="flex-grow-1">
                                    <h6 className="fw-bold text-primary mb-2">
                                      {plan.plan_name}
                                    </h6>
                                    <p className="card-text text-secondary small mb-2 lh-sm">
                                      {plan.desc}
                                    </p>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                      {plan.rs && Object.entries(plan.rs).map(([duration, price]) => (
                                        <div key={duration} className="d-flex align-items-center bg-light border rounded px-2 py-1">
                                          <span className="fw-bold text-dark me-2 small">
                                            {duration}: ₹{price}
                                          </span>
                                          <button
                                            className="btn btn-sm btn-primary rounded-pill px-2.5 py-0.5"
                                            onClick={() => handleDthChannelSelect(price)}
                                            style={{ fontSize: '11px' }}
                                          >
                                            Select
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                    {plan.last_update && (
                                      <small className="text-muted extra-small">
                                        Updated: {plan.last_update}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {filterDthChannelPlans(plans).length === 0 && (
                          <div className="text-center py-5 text-muted">
                            No channel plans found matching your search.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-5 text-muted">
                Click "Channel Plans" to load available channel plans.
              </div>
            )}
          </div>
        </div>

        {/* Offcanvas for DTH R Offer */}
        <div 
          className="offcanvas offcanvas-bottom rounded-top-4 border-0 shadow-lg"
          id="offcanvasBottom3"
          tabIndex="-1"
          style={{ height: '75vh', maxWidth: '800px', margin: '0 auto', backgroundColor: '#ffffff' }}
        >
          <div className="offcanvas-header border-bottom py-3 px-4 bg-white sticky-top">
            <h5 className="offcanvas-title fw-bold text-dark mb-0">DTH R Offer</h5>
            <button 
              className="btn-close text-secondary"
              data-bs-dismiss="offcanvas"
              type="button"
              aria-label="Close"
            ></button>
          </div>
          <div className="offcanvas-body p-4" style={{ backgroundColor: '#f8f9fa' }}>
            {dthROfferLoading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted small">Fetching DTH R-Offers...</p>
              </div>
            ) : dthROfferData ? (
              <>
                {/* Search Box */}
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control form-control-lg bg-white border shadow-sm fs-6"
                    placeholder="🔍 Search R Offer..."
                    value={dthROfferSearchTerm}
                    onChange={(e) => setDthROfferSearchTerm(e.target.value)}
                  />
                </div>

                <div className="d-flex flex-column gap-3">
                  {Array.isArray(dthROfferData) ? (
                    filterDthROfferData(dthROfferData).map((offer, index) => (
                      <div key={index} className="card plan-card border shadow-sm rounded-3 overflow-hidden bg-white">
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-center gap-3">
                            <div className="flex-grow-1">
                              <h6 className="fw-bold text-primary mb-1">
                                {offer.plan_name || 'DTH Offer'}
                              </h6>
                              <p className="card-text text-secondary small mb-2 lh-sm">
                                {offer.desc}
                              </p>
                              {offer.rs && (
                                <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1 me-2">
                                  ₹{offer.rs}
                                </span>
                              )}
                            </div>
                            {offer.rs && (
                              <button
                                className="btn btn-outline-primary px-3 rounded-pill fw-semibold flex-shrink-0"
                                onClick={() => handleDthROfferSelect(offer.rs)}
                              >
                                Select
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="card border shadow-sm rounded-3 bg-white">
                      <div className="card-body text-center py-4">
                        <p className="text-muted mb-0">{dthROfferData.desc || 'No R Offer data available'}</p>
                      </div>
                    </div>
                  )}
                </div>

                {Array.isArray(dthROfferData) && filterDthROfferData(dthROfferData).length === 0 && (
                  <div className="text-center py-5 text-muted">
                    No R Offers found matching your search.
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-5 text-muted">
                Click "DTH R Offer" to load available offers.
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        /* DTH R Offer Button */
        .dth-action-btn-roffer {
          background-color: #e0f2fe !important;
          color: #0284c7 !important;
          border-radius: 50px !important;
          font-size: 12.5px !important;
          font-weight: 700 !important;
          border: 1px solid #93c5fd !important;
          transition: all 0.25s ease !important;
          box-shadow: 0 2px 6px rgba(2, 132, 199, 0.08) !important;
        }
        .dth-action-btn-roffer:hover {
          background-color: #bae6fd !important;
          color: #0369a1 !important;
          transform: translateY(-1.5px) !important;
          box-shadow: 0 4px 12px rgba(2, 132, 199, 0.18) !important;
        }

        /* Channel Plans Button */
        .dth-action-btn-channel {
          background-color: #f3e8ff !important;
          color: #7e57c2 !important;
          border-radius: 50px !important;
          font-size: 12.5px !important;
          font-weight: 700 !important;
          border: 1px solid #d8b4fe !important;
          transition: all 0.25s ease !important;
          box-shadow: 0 2px 6px rgba(126, 87, 194, 0.08) !important;
        }
        .dth-action-btn-channel:hover {
          background-color: #e9d5ff !important;
          color: #6b21a8 !important;
          transform: translateY(-1.5px) !important;
          box-shadow: 0 4px 12px rgba(126, 87, 194, 0.18) !important;
        }

        /* Fetch Plan Button */
        .dth-action-btn-plan {
          background-color: #fce7f3 !important;
          color: #c026d3 !important;
          border-radius: 50px !important;
          font-size: 12.5px !important;
          font-weight: 700 !important;
          border: 1px solid #fbcfe8 !important;
          transition: all 0.25s ease !important;
          box-shadow: 0 2px 6px rgba(192, 38, 211, 0.08) !important;
        }
        .dth-action-btn-plan:hover {
          background-color: #fbcfe8 !important;
          color: #9d174d !important;
          transform: translateY(-1.5px) !important;
          box-shadow: 0 4px 12px rgba(192, 38, 211, 0.18) !important;
        }

        /* Heavy Refresh Button */
        .dth-action-btn-refresh {
          background-color: #fef3c7 !important;
          color: #d97706 !important;
          border-radius: 50px !important;
          font-size: 12.5px !important;
          font-weight: 700 !important;
          border: 1px solid #fde68a !important;
          transition: all 0.25s ease !important;
          box-shadow: 0 2px 6px rgba(217, 119, 6, 0.08) !important;
        }
        .dth-action-btn-refresh:hover {
          background-color: #fde68a !important;
          color: #b45309 !important;
          transform: translateY(-1.5px) !important;
          box-shadow: 0 4px 12px rgba(217, 119, 6, 0.18) !important;
        }

        /* Submit Button */
        .btn-dth-submit {
          background: linear-gradient(135deg, #7e57c2 0%, #673ab7 100%) !important;
          color: #ffffff !important;
          border: none !important;
          border-radius: 12px !important;
          transition: all 0.25s ease !important;
          box-shadow: 0 4px 12px rgba(126, 87, 194, 0.25) !important;
        }
        .btn-dth-submit:hover {
          background: linear-gradient(135deg, #6e46b2 0%, #5b2fa0 100%) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 6px 18px rgba(126, 87, 194, 0.35) !important;
        }

        /* Reset Button */
        .btn-dth-reset {
          background-color: #f8fafc !important;
          color: #475569 !important;
          border: 1px solid #cbd5e1 !important;
          border-radius: 12px !important;
          transition: all 0.25s ease !important;
        }
        .btn-dth-reset:hover {
          background-color: #f1f5f9 !important;
          color: #1e293b !important;
          border-color: #94a3b8 !important;
          transform: translateY(-2px) !important;
        }

        .plan-card:hover {
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          transform: translateY(-2px);
          transition: all 0.3s ease;
        }
        
        .offcanvas-body {
          max-height: calc(80vh - 60px);
          overflow-y: auto;
        }

        .badge {
          font-size: 0.75em;
        }
      `}</style>
    </main>
  );
}

export default DTHRecharge;